import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, TextField, InputAdornment,
  Chip, Button, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, TableSortLabel, IconButton,
  Select, MenuItem, FormControl, InputLabel, Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon    from "@mui/icons-material/Add";
import CloseIcon  from "@mui/icons-material/Close";
import BadgeIcon  from "@mui/icons-material/Badge";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useSnackbar } from "notistack";
import { useContentSetting } from "../../Hooks/ContentSetting";
import { get_school_staff_api, add_school_staff_api, update_school_staff_api, get_staff_progress_api } from "../../DAL/SchoolPortal/SchoolPortal";
import moment from "moment";
import SchoolStaffDetailDrawer from "../../components/SchoolStaffDetailDrawer";
import StatTile from "../../layouts/schoolPortal/StatTile";
import { ROLE_DISPLAY, getRoleDisplay } from "../../utils/roleDisplay";
import {
  surface, border, textPrimary, textSecondary, textMuted, accent,
  font, inputSx, thSx, rowSx, shadowMed, primaryButtonSx, secondaryButtonSx, dialogPaperSx,
  success, avatarColorFor, warning, surfaceRaised, shadow,
} from "../../layouts/schoolPortal/schoolPortalTheme";
import InstructorCourses from "./Tutors/InstructorCourses";
import { useSchool } from "../../Hooks/SchoolContext";

// Owner (primary_admin) is excluded — a school has exactly one, and
// transferring ownership needs a dedicated flow (it also updates
// FlightSchool.primaryAdmin), not a plain role-field edit like the others.
const ASSIGNABLE_ROLES = ["staff_admin", "operations", "instructor"];

// ── Team progress ─────────────────────────────────────────────────────────────

/**
 * What each person has actually done, above the list of who they are.
 *
 * The page could add, re-role and remove people and say nothing about their
 * work — "is Dana keeping up" meant logging in as Dana. One card per person
 * who teaches, attention first: owed debriefs lead, because on a gated course
 * each one is a student who cannot move on. People who do not teach and have
 * nothing to show are left out rather than drawn as a row of zeros.
 */
function Metric({ label, value, sub, tone }) {
  return (
    <Box sx={{ backgroundColor: surfaceRaised, borderRadius: "8px", px: 1, py: 0.75, minWidth: 0 }}>
      <Typography sx={{ fontFamily: font, fontSize: "18px", fontWeight: 800, color: tone || textPrimary, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Typography>
      <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: textSecondary, mt: 0.2, lineHeight: 1.25 }}>
        {label}
      </Typography>
      {sub && (
        <Typography sx={{ fontFamily: font, fontSize: "10px", color: textMuted, lineHeight: 1.25 }}>{sub}</Typography>
      )}
    </Box>
  );
}

function TeamProgress({ data, days, onDays, loading, onOpen, isGroup }) {
  const people = (data?.staff || []).filter((p) => p.teaches || p.sessionsTaught || p.notesLogged);
  return (
    <Box sx={{ mb: 3.5 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: 1.25, flexWrap: "wrap" }}>
        <Typography sx={{ fontFamily: font, fontSize: "16px", fontWeight: 800, color: textPrimary }}>
          How your {isGroup ? "team" : "staff"} are doing
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5, ml: "auto" }}>
          {[7, 30, 90].map((d) => (
            <Box
              key={d} role="button" tabIndex={0}
              onClick={() => onDays(d)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onDays(d); } }}
              sx={{
                px: 1, py: 0.25, borderRadius: "6px", cursor: "pointer", fontFamily: font, fontSize: "11.5px", fontWeight: 700,
                border: `1px solid ${days === d ? accent : border}`,
                backgroundColor: days === d ? accent : surface, color: days === d ? "#fff" : textSecondary,
              }}
            >
              {d} days
            </Box>
          ))}
        </Box>
      </Box>

      {loading && <CircularProgress size={18} sx={{ color: accent }} />}

      {!loading && !people.length && (
        <Typography sx={{ fontFamily: font, fontSize: "13px", color: textMuted }}>
          Nobody who teaches is on the {isGroup ? "team" : "staff"} yet. Their sessions, debriefs and sign-offs will show here.
        </Typography>
      )}

      {!loading && people.length > 0 && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }, gap: 1.5 }}>
          {people.map((p) => (
            <Box
              key={p.id} role="button" tabIndex={0}
              onClick={() => onOpen(p.id)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(p.id); } }}
              sx={{
                backgroundColor: surface, border: `1px solid ${border}`, borderRadius: "12px", boxShadow: shadow,
                p: 1.5, cursor: "pointer", borderLeft: `3px solid ${p.debriefsOwed ? warning : "transparent"}`,
                "&:hover": { borderColor: accent },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.1 }}>
                <Box sx={{
                  width: 28, height: 28, borderRadius: "8px", flexShrink: 0, backgroundColor: avatarColorFor(p.name),
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Typography sx={{ fontSize: "10.5px", fontWeight: 700, color: "#fff", fontFamily: font }}>
                    {p.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography noWrap sx={{ fontFamily: font, fontSize: "13.5px", fontWeight: 700, color: textPrimary }}>{p.name}</Typography>
                  <Typography noWrap sx={{ fontFamily: font, fontSize: "11px", color: textMuted }}>
                    {getRoleDisplay(p.role).label}
                    {p.teaches && !p.bookable ? " · not taking bookings" : ""}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 0.75 }}>
                <Metric label="students" value={p.studentsInWindow} sub={`${p.studentsAll} ever`} />
                <Metric label="sessions" value={p.sessionsTaught} sub={`${p.hoursTaught}h`} />
                <Metric label="debriefs owed" value={p.debriefsOwed} tone={p.debriefsOwed ? warning : success} />
                <Metric label="coming up" value={p.upcoming} sub={p.awaitingAnswer ? `${p.awaitingAnswer} to answer` : null} />
              </Box>

              <Typography sx={{ fontFamily: font, fontSize: "11px", color: textSecondary, mt: 1, lineHeight: 1.45 }}>
                {[
                  p.signedOff ? `${p.signedOff} lesson${p.signedOff === 1 ? "" : "s"} signed off` : null,
                  p.notYet ? `${p.notYet} not yet` : null,
                  p.notesLogged ? `${p.notesLogged} note${p.notesLogged === 1 ? "" : "s"}` : null,
                  p.lastTaughtAt ? `last taught ${moment(p.lastTaughtAt).fromNow()}` : "has not taught yet",
                ].filter(Boolean).join(" · ")}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function SchoolStaff() {
  const { hasModule, isIndependentInstructor, staffLimit } = useSchool();
  const { enqueueSnackbar } = useSnackbar();
  const { userInfo } = useContentSetting();
  const canManageRoles = userInfo?.role === "primary_admin";
  // Adding and removing people is an owner's or manager's act; the server now
  // enforces the same. A manager may not create another manager.
  const canAddStaff = ["primary_admin", "staff_admin"].includes(userInfo?.role);
  const addableRoles = canManageRoles ? ASSIGNABLE_ROLES : ASSIGNABLE_ROLES.filter((r) => r !== "staff_admin");
  const [isLoading,    setIsLoading]    = useState(true);
  const [staff,        setStaff]        = useState([]);
  const [searchQuery,  setSearchQuery]  = useState("");
  const [addOpen,      setAddOpen]      = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newAdmin,     setNewAdmin]     = useState({ firstName: "", lastName: "", email: "", role: "staff_admin" });
  const [drawerOpen,   setDrawerOpen]   = useState(false);
  const [selected,     setSelected]     = useState(null);
  const [orderBy,      setOrderBy]      = useState("name");
  const [orderDir,     setOrderDir]     = useState("asc");
  const [roleSavingId, setRoleSavingId] = useState(null);
  const [progress,     setProgress]     = useState(null);
  const [progressDays, setProgressDays] = useState(30);
  const [progressLoading, setProgressLoading] = useState(true);

  useEffect(() => { fetchStaff(); }, []);

  useEffect(() => {
    let cancelled = false;
    setProgressLoading(true);
    get_staff_progress_api(progressDays)
      .then((r) => { if (!cancelled) setProgress(r?.code === 200 ? r.data : null); })
      .catch(() => { if (!cancelled) setProgress(null); })
      .finally(() => { if (!cancelled) setProgressLoading(false); });
    return () => { cancelled = true; };
  }, [progressDays]);

  const progressById = useMemo(
    () => new Map((progress?.staff || []).map((p) => [String(p.id), p])),
    [progress],
  );

  const fetchStaff = async () => {
    try {
      const r = await get_school_staff_api();
      if (r.code === 200) setStaff(r.staff || []);
      else enqueueSnackbar(r.message || "Failed to fetch staff", { variant: "error" });
    } catch { enqueueSnackbar("Failed to fetch staff", { variant: "error" }); }
    finally { setIsLoading(false); }
  };

  const handleSort = (col) => {
    if (orderBy === col) setOrderDir(orderDir === "asc" ? "desc" : "asc");
    else { setOrderBy(col); setOrderDir("asc"); }
  };

  const rows = useMemo(() => {
    const filtered = staff.filter((m) => {
      const name = `${m.firstName || m.first_name || ""} ${m.lastName || m.last_name || ""}`.toLowerCase();
      return name.includes(searchQuery.toLowerCase()) || (m.email || "").toLowerCase().includes(searchQuery.toLowerCase());
    });
    return [...filtered].sort((a, b) => {
      let v = 0;
      if (orderBy === "name") v = `${a.firstName||a.first_name||""} ${a.lastName||a.last_name||""}`.localeCompare(`${b.firstName||b.first_name||""} ${b.lastName||b.last_name||""}`);
      else if (orderBy === "email")     v = (a.email||"").localeCompare(b.email||"");
      else if (orderBy === "joined")    v = new Date(a.createdAt||0) - new Date(b.createdAt||0);
      else if (orderBy === "lastLogin") v = new Date(a.lastLogin||0) - new Date(b.lastLogin||0);
      else if (orderBy === "status")    v = (a.status||"").localeCompare(b.status||"");
      return orderDir === "asc" ? v : -v;
    });
  }, [staff, searchQuery, orderBy, orderDir]);

  const getName = (m) => `${m.firstName||m.first_name||""} ${m.lastName||m.last_name||""}`.trim() || m.email?.split("@")[0] || "Unknown";
  const getInitials = (m) => `${(m.firstName||m.first_name||"")[0]||""}${(m.lastName||m.last_name||"")[0]||""}`.toLocaleUpperCase() || "?";

  const counts = useMemo(() => ({
    total: staff.length,
    active: staff.filter((m) => (m.status || "active") === "active").length,
  }), [staff]);

  // Independent instructor accounts are capped (staffLimit, normally 2). The
  // count mirrors the server's rule: everyone not terminated, owner included.
  const nonTerminated = useMemo(() => staff.filter((m) => m.status !== "terminated").length, [staff]);
  const atStaffLimit = staffLimit != null && nonTerminated >= staffLimit;
  const staffLimitMessage = `Independent instructor accounts can have up to ${staffLimit} team members.`;
  const pageTitle = isIndependentInstructor ? "Team" : "Staff Members";

  const statusChipSx = (status) => {
    const map = { active: { bg: "rgba(22,163,74,0.1)", color: success }, inactive: { bg: "rgba(26,26,26,0.07)", color: textSecondary }, suspended: { bg: "rgba(220,38,38,0.1)", color: "#dc2626" } };
    return map[status] || map.inactive;
  };

  const handleAdd = async () => {
    if (!newAdmin.firstName || !newAdmin.lastName || !newAdmin.email) {
      enqueueSnackbar("Please fill in all required fields", { variant: "error" });
      return;
    }
    setIsSubmitting(true);
    try {
      const r = await add_school_staff_api(newAdmin);
      if (r.code === 201 || r.admin) {
        enqueueSnackbar(`Activation email sent to ${newAdmin.email}`, { variant: "success" });
        setAddOpen(false);
        fetchStaff();
      } else enqueueSnackbar(r.message || "Failed to add staff", { variant: "error" });
    } catch { enqueueSnackbar("Failed to add staff", { variant: "error" }); }
    finally { setIsSubmitting(false); }
  };

  const handleRoleChange = async (staffId, newRole) => {
    setRoleSavingId(staffId);
    try {
      const r = await update_school_staff_api(staffId, { role: newRole });
      if (r.code === 200 || r.staff) {
        setStaff((prev) => prev.map((m) => (m._id === staffId ? { ...m, role: newRole } : m)));
        enqueueSnackbar("Role updated", { variant: "success" });
      } else enqueueSnackbar(r.message || "Failed to update role", { variant: "error" });
    } catch { enqueueSnackbar("Failed to update role", { variant: "error" }); }
    finally { setRoleSavingId(null); }
  };

  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}><CircularProgress sx={{ color: "#c01e2e" }} /></Box>;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: { xs: "22px", sm: "26px" }, fontWeight: 800, color: textPrimary, fontFamily: font, letterSpacing: "-0.3px", lineHeight: 1.15 }}>{pageTitle}</Typography>
          <Typography sx={{ fontSize: "13px", color: textSecondary, fontFamily: font, mt: 0.5 }}>
            {staff.length} {isIndependentInstructor ? "team" : "staff"} member{staff.length === 1 ? "" : "s"}
            {staffLimit != null ? ` of ${staffLimit}` : ""}
          </Typography>
        </Box>
        {canAddStaff && (
          <Tooltip title={atStaffLimit ? staffLimitMessage : ""} arrow>
            <span>
              <Button variant="contained" color="inherit" startIcon={<AddIcon />} disabled={atStaffLimit}
                onClick={() => { setNewAdmin({ firstName: "", lastName: "", email: "", role: addableRoles[0] }); setAddOpen(true); }}
                sx={primaryButtonSx}>
                {isIndependentInstructor ? "Add team member" : "Add Staff"}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {atStaffLimit && (
        <Box sx={{ mb: 3, p: 1.5, borderRadius: "8px", border: `1px solid ${border}`, backgroundColor: surface }}>
          <Typography sx={{ fontFamily: font, fontSize: "13px", color: textSecondary }}>
            {staffLimitMessage}
          </Typography>
        </Box>
      )}

      {progress !== null || progressLoading ? (
        <TeamProgress
          data={progress} days={progressDays} onDays={setProgressDays} loading={progressLoading}
          isGroup={isIndependentInstructor}
          onOpen={(id) => {
            const m = staff.find((x) => String(x._id) === String(id));
            if (m) { setSelected(m); setDrawerOpen(true); }
          }}
        />
      ) : null}

      {/* Stat tiles */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 2, mb: 3, maxWidth: { sm: 480 } }}>
        <StatTile icon={<BadgeIcon />} label="Total Staff" value={counts.total} color={accent} />
        <StatTile icon={<CheckCircleIcon />} label="Active" value={counts.active} color={success} />
      </Box>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField placeholder="Search staff…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ ...inputSx, width: { xs: "100%", sm: "300px" } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: textMuted, fontSize: 18 }} /></InputAdornment> }} />
      </Box>

      {/* Table */}
      <Card sx={{ backgroundColor: surface, border: `1px solid ${border}`, borderRadius: "14px", boxShadow: shadowMed, backgroundImage: "none" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {[{ id: "name", label: "Name" }, { id: "email", label: "Email" }, { id: "role", label: "Role", noSort: true }, { id: "joined", label: "Joined" }, { id: "lastLogin", label: "Last Login" }, { id: "status", label: "Status" }].map(({ id, label, noSort }) => (
                  <TableCell key={id} sx={thSx}>
                    {noSort ? label : (
                      <TableSortLabel active={orderBy === id} direction={orderBy === id ? orderDir : "asc"} onClick={() => handleSort(id)}>
                        {label}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((m) => (
                <TableRow key={m._id} onClick={() => { setSelected(m); setDrawerOpen(true); }} sx={rowSx}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                      <Box sx={{
                        width: 30, height: 30, borderRadius: "9px", flexShrink: 0,
                        backgroundColor: avatarColorFor(getName(m)), display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#fff", fontFamily: font }}>
                          {getInitials(m)}
                        </Typography>
                      </Box>
                      <Typography sx={{ color: textPrimary, fontSize: "14px", fontWeight: 500, fontFamily: font }}>{getName(m)}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: "#6b6b6b", fontSize: "13px", fontFamily: font }}>{m.email}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {m.role === "primary_admin" || !canManageRoles ? (
                      <Tooltip title={getRoleDisplay(m.role).powers} arrow placement="top">
                        <Chip label={getRoleDisplay(m.role).label} size="small"
                          sx={{ borderRadius: "6px", fontSize: "11px", fontWeight: 500, fontFamily: font, backgroundColor: "rgba(192,30,46,0.1)", color: "#c01e2e", cursor: "help" }} />
                      </Tooltip>
                    ) : (
                      <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select
                          value={m.role || "staff_admin"}
                          onChange={(e) => handleRoleChange(m._id, e.target.value)}
                          disabled={roleSavingId === m._id}
                          sx={{
                            fontSize: "12px", fontFamily: font, borderRadius: "8px",
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(26,26,26,0.14)" },
                          }}
                        >
                          {ASSIGNABLE_ROLES.map((r) => (
                            <MenuItem key={r} value={r} sx={{ fontSize: "13px", fontFamily: font }}>
                              {ROLE_DISPLAY[r].label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </TableCell>
                  <TableCell sx={{ color: "#6b6b6b", fontSize: "13px", fontFamily: font }}>{m.createdAt ? new Date(m.createdAt).toLocaleDateString() : "—"}</TableCell>
                  <TableCell sx={{ color: "#6b6b6b", fontSize: "13px", fontFamily: font }}>{m.lastLogin ? new Date(m.lastLogin).toLocaleDateString() : "Never"}</TableCell>
                  <TableCell>
                    <Chip label={m.status || "active"} size="small" sx={{ backgroundColor: statusChipSx(m.status||"active").bg, color: statusChipSx(m.status||"active").color, fontSize: "11px", fontWeight: 500, fontFamily: font, borderRadius: "999px", textTransform: "capitalize" }} />
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center", py: 7, borderBottom: "none" }}>
                    <BadgeIcon sx={{ fontSize: 36, color: border, mb: 1 }} />
                    <Typography sx={{ color: textMuted, fontFamily: font, fontSize: "13px" }}>
                      {searchQuery ? "No staff match your search" : "No staff yet — add your first one above"}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Add Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: textPrimary, fontFamily: font }}>
          Add Staff Admin
          <IconButton onClick={() => setAddOpen(false)} sx={{ color: textMuted, "&:hover": { color: textPrimary } }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {["First Name", "Last Name", "Email"].map((label, i) => {
              const field = ["firstName", "lastName", "email"][i];
              return (
                <TextField key={field} label={label} type={field === "email" ? "email" : "text"} fullWidth required
                  value={newAdmin[field]} onChange={(e) => setNewAdmin({ ...newAdmin, [field]: e.target.value })} sx={inputSx} />
              );
            })}
            <FormControl fullWidth sx={inputSx}>
              <InputLabel>Role</InputLabel>
              <Select
                value={newAdmin.role}
                label="Role"
                onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })}
              >
                {addableRoles.map((r) => (
                  <MenuItem key={r} value={r}>{ROLE_DISPLAY[r].label} — {ROLE_DISPLAY[r].powers}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button onClick={() => setAddOpen(false)} sx={secondaryButtonSx}>Cancel</Button>
          <Button onClick={handleAdd} variant="contained" color="inherit" disabled={isSubmitting} sx={primaryButtonSx}>
            {isSubmitting ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Add Staff"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Who may teach what. Lives on Staff rather than in Tutors settings
          because it is a fact about a person, and this is the page someone
          opens when they are thinking about a person. */}
      {hasModule("tutors") && (
        <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${border}` }}>
          <Typography sx={{ fontSize: "16px", fontWeight: 800, color: textPrimary, fontFamily: font, mb: 0.5 }}>
            Course approvals
          </Typography>
          <InstructorCourses />
        </Box>
      )}

      <SchoolStaffDetailDrawer
        open={drawerOpen} onClose={() => { setDrawerOpen(false); setSelected(null); }}
        staffMember={selected} onUpdate={fetchStaff} totalStaffCount={staff.length}
        progress={selected ? progressById.get(String(selected._id)) : null}
        windowDays={progress?.windowDays || progressDays}
      />
    </Box>
  );
}
